/**
 * RestaurateurPendingPOS — Encaissement fidélité V2
 *
 * Flow :
 *   1. Le serveur saisit le montant (et optionnellement les plats de la carte)
 *   2. « Prêt » → createPendingTransaction → écran d'attente
 *   3. Le client relie l'addition à son compte :
 *      - addition d'une réservation : le client de la réservation tape la puce, c'est d'office ;
 *      - addition sans réservation : il tape la puce puis saisit le CODE à 6 chiffres affiché
 *        ici (le tap ne montre jamais les additions des autres clients). Trop de codes erronés
 *        dans le restaurant → saisie du code suspendue 15 min (bandeau ci-dessous), l'addition
 *        reste valable et se relie par le QR ;
 *      - secours sans NFC : le serveur scanne le QR wallet du client depuis cet écran
 *        → validateQrSession(pendingTransactionId), sans code ;
 *      - client SANS l'app : il tape la puce, arrive sur la page /t et y saisit le code →
 *        l'addition est gardée 72 h pour lui (claimMethod 'guest_claim', guestCheckin) ;
 *        il la récupère dans l'app après son inscription (redeemGuestClaim).
 *   4. onSnapshot détecte status 'claimed' → confirmation en temps réel ('cancelled' après
 *      « Annuler », 'expired' au bout de 5 min).
 *
 * Plats : seuls les plats de la carte sont envoyés (le serveur reprend nom et prix de la
 * carte) ; un extra « hors carte » ne compte que dans le montant.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { firestoreService } from '../../shared/services/firestoreService';
import { CheckIcon, XIcon, GiftIcon, ClockIcon, WarningIcon } from './icons/UiIcons';
import DishPicker from './shared/DishPicker';
import ClientQrScanner from './ClientQrScanner';
import { getBackendService } from '../../shared/services/apiConfig';
import type { MenuItem, DishSnapshot } from '../../shared/types';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PosItem {
    /** Plat de la carte (envoyé au serveur) ; absent = extra hors carte (montant seulement). */
    menuItemId?: string;
    name: string;
    quantity: number;
    unitPrice: string; // euros, saisie utilisateur
}

type PosState = 'input' | 'waiting' | 'success' | 'expired' | 'error';

interface SuccessData {
    clientName?: string;
    pointsAwarded: number;
    newPointsTotal?: number;
    programType?: string;
    membershipStatus?: 'new' | 'existing' | 'reward_unlocked';
    /** Gardée par un client sans l'app (page /t) : crédit à son inscription, avant cette date (ms). */
    guestUntil?: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCountdown(ms: number): string {
    if (ms <= 0) return '0:00';
    const totalSeconds = Math.ceil(ms / 1000);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/** « 482913 » → « 482 913 », plus lisible à dicter et à recopier. */
function formatBillCode(code: string): string {
    return code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}

function msOf(v: unknown): number | undefined {
    if (v && typeof (v as { toMillis?: unknown }).toMillis === 'function') return (v as { toMillis: () => number }).toMillis();
    return typeof v === 'number' ? v : undefined;
}

/** « jeudi 30 octobre à 21:30 ». */
function formatUntil(ms: number): string {
    const d = new Date(ms);
    return `${d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

function gainLabel(programType: string | undefined, n: number): string {
    if (programType === 'stamps') return n > 1 ? 'tampons attribués' : 'tampon attribué';
    return 'points attribués';
}

// ── Component ─────────────────────────────────────────────────────────────────

interface RestaurateurPendingPOSProps {
    restaurantId: string;
    /** When set, links the pending transaction to this reservation. The Cloud Function
     *  will mark the reservation `completed` atomically when the client claims via NFC. */
    linkedReservationId?: string;
    /** Pre-populates the amount input (in centimes). */
    prefilledAmountCents?: number;
    /** Pre-populates the items list. */
    prefilledItems?: DishSnapshot[];
    /**
     * Si vrai et que `prefilledAmountCents` est valide, le formulaire est court-circuité :
     * la pending tx est créée immédiatement au mount sans demander de re-saisie au
     * restaurateur. Utilisé quand le composant est rendu DEPUIS la modal "Compléter
     * réservation" qui a déjà capturé le montant à l'étape précédente.
     */
    autoSubmit?: boolean;
    /** Called when the pending transaction is successfully claimed by the client. */
    onSuccess?: (data: SuccessData) => void;
}

const RestaurateurPendingPOS: React.FC<RestaurateurPendingPOSProps> = ({
    restaurantId,
    linkedReservationId,
    prefilledAmountCents,
    prefilledItems,
    autoSubmit,
    onSuccess,
}) => {
    const [posState, setPosState] = useState<PosState>('input');
    const [amountEuros, setAmountEuros] = useState(
        typeof prefilledAmountCents === 'number' && prefilledAmountCents > 0
            ? (prefilledAmountCents / 100).toFixed(2)
            : '',
    );
    const [items, setItems] = useState<PosItem[]>(
        prefilledItems && prefilledItems.length > 0
            ? prefilledItems.map(i => ({
                ...(i.menuItemId ? { menuItemId: i.menuItemId } : {}),
                name: i.name, quantity: i.quantity, unitPrice: (i.unitPrice / 100).toFixed(2),
            }))
            : [],
    );
    const [formError, setFormError] = useState('');
    const [menu, setMenu] = useState<MenuItem[]>([]);
    // Montant saisi à la main (ou reçu de la modal réservation) : on ne l'écrase plus avec
    // le total des plats. Posé à la première MODIFICATION du champ (pas au simple focus).
    const [amountTouched, setAmountTouched] = useState(
        typeof prefilledAmountCents === 'number' && prefilledAmountCents > 0,
    );

    // Carte du restaurant : le serveur choisit les plats depuis la carte (extra hors carte en secours).
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const backend = await getBackendService();
                const r = await backend.getRestaurant(restaurantId);
                if (r && mounted) setMenu(((r as any).menu as MenuItem[] | undefined) || []);
            } catch (e) {
                console.warn('Could not load menu for POS picker', e);
            }
        })();
        return () => { mounted = false; };
    }, [restaurantId]);
    const [creating, setCreating] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const [pendingTxId, setPendingTxId] = useState<string | null>(null);
    const [billCode, setBillCode] = useState<string | null>(null);
    const [countdown, setCountdown] = useState('5:00');
    const [successData, setSuccessData] = useState<SuccessData | null>(null);
    const [errorMsg, setErrorMsg] = useState('');
    // Secours sans NFC : scan du QR wallet du client pour CETTE addition.
    const [showQrScanner, setShowQrScanner] = useState(false);
    // Saisie du code suspendue pour le restaurant (trop d'erreurs) : fin de la suspension (ms).
    const [codeSuspendedUntil, setCodeSuspendedUntil] = useState(0);
    // Page web de la puce (clients sans l'app) sous protection après des codes faux répétés.
    const [guestProtectedUntil, setGuestProtectedUntil] = useState(0);
    const [nowTick, setNowTick] = useState(Date.now());

    useEffect(() => {
        if (posState !== 'waiting' || !billCode) return;
        const unsub = firestoreService.onBillCodeGuardUpdate(restaurantId, setCodeSuspendedUntil);
        const unsubGuest = firestoreService.onGuestCodeGuardUpdate(restaurantId, setGuestProtectedUntil);
        const t = setInterval(() => setNowTick(Date.now()), 15_000);
        return () => { unsub(); unsubGuest(); clearInterval(t); };
    }, [posState, billCode, restaurantId]);
    const codeSuspended = codeSuspendedUntil > nowTick;
    const guestProtected = guestProtectedUntil > nowTick;

    const unsubscribeRef = useRef<(() => void) | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // ── Cleanup ───────────────────────────────────────────────────────────────

    const cleanup = useCallback(() => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => () => cleanup(), [cleanup]);

    // Auto-submit : on évite la re-saisie quand le montant a déjà été capturé
    // à l'étape "Détails" du flow Compléter réservation. On déclenche au mount uniquement.
    const autoSubmitTriggeredRef = useRef(false);
    useEffect(() => {
        if (autoSubmitTriggeredRef.current) return;
        if (!autoSubmit) return;
        if (typeof prefilledAmountCents !== 'number' || prefilledAmountCents <= 0) return;
        autoSubmitTriggeredRef.current = true;
        // Petit timeout pour laisser le state initial se poser
        const t = setTimeout(() => {
            handleSubmit({ preventDefault: () => {} } as React.FormEvent);
        }, 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSubmit, prefilledAmountCents]);

    // ── Countdown timer ───────────────────────────────────────────────────────

    const startCountdown = useCallback((expMs: number) => {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            const remaining = expMs - Date.now();
            if (remaining <= 0) {
                clearInterval(timerRef.current!);
                timerRef.current = null;
                setCountdown('0:00');
                setShowQrScanner(false);
                setPosState('expired');
                cleanup();
            } else {
                setCountdown(formatCountdown(remaining));
            }
        }, 500);
    }, [cleanup]);

    // ── Items management ──────────────────────────────────────────────────────

    const addItem = () => setItems((prev) => [...prev, { name: '', quantity: 1, unitPrice: '' }]);

    const catalogItems: DishSnapshot[] = items
        .filter((i) => i.menuItemId)
        .map((i) => ({ menuItemId: i.menuItemId, name: i.name, quantity: i.quantity, unitPrice: Math.round(parseFloat(i.unitPrice) * 100) }));

    const addCatalogItem = (item: MenuItem) => {
        setItems((prev) => {
            const idx = prev.findIndex((p) => p.menuItemId === item.id);
            if (idx >= 0) return prev.map((p, i) => (i === idx ? { ...p, quantity: p.quantity + 1 } : p));
            return [...prev, { menuItemId: item.id, name: item.name, quantity: 1, unitPrice: item.price.toFixed(2) }];
        });
    };
    const removeCatalogItem = (itemName: string) => {
        setItems((prev) => {
            const idx = prev.findIndex((p) => p.menuItemId && p.name === itemName);
            if (idx < 0) return prev;
            if (prev[idx].quantity <= 1) return prev.filter((_, i) => i !== idx);
            return prev.map((p, i) => (i === idx ? { ...p, quantity: p.quantity - 1 } : p));
        });
    };

    // Tant que le serveur n'a pas saisi le montant à la main, il suit le total des plats.
    useEffect(() => {
        if (amountTouched) return;
        const total = items.reduce((acc, i) => {
            const price = parseFloat(i.unitPrice);
            return acc + (isNaN(price) ? 0 : price * (Number(i.quantity) || 0));
        }, 0);
        setAmountEuros(total > 0 ? total.toFixed(2) : '');
    }, [items, amountTouched]);
    const freeItems = items.map((item, idx) => ({ item, idx })).filter(({ item }) => !item.menuItemId);
    const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));
    const updateItem = (idx: number, field: keyof PosItem, value: string | number) =>
        setItems((prev) => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));

    // ── Real-time listener ────────────────────────────────────────────────────

    const listenTo = (txId: string) => {
        unsubscribeRef.current = firestoreService.onPendingTransactionUpdate(txId, (data) => {
            if (data.status === 'claimed') {
                cleanup();
                const guest = data.claimMethod === 'guest_claim' && !data.claimedBy;
                const success: SuccessData = {
                    clientName: data.clientName as string | undefined,
                    pointsAwarded: (data.pointsAwarded as number) ?? 0,
                    newPointsTotal: data.newPointsTotal as number | undefined,
                    programType: data.programType as string | undefined,
                    membershipStatus: data.membershipStatus as SuccessData['membershipStatus'],
                    ...(guest ? { guestUntil: msOf(data.guestClaimExpiresAt) ?? Date.now() + 72 * 3600_000 } : {}),
                };
                setShowQrScanner(false);
                setSuccessData(success);
                setPosState('success');
                onSuccess?.(success);
            } else if (data.status === 'expired') {
                cleanup();
                setShowQrScanner(false);
                setPosState('expired');
            }
        });
    };

    // ── Submit ────────────────────────────────────────────────────────────────

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        const amountCentimes = Math.round(parseFloat(amountEuros) * 100);
        if (!amountEuros || isNaN(amountCentimes) || amountCentimes <= 0) {
            setFormError('Montant invalide');
            return;
        }
        if (amountCentimes >= 100_000) {
            setFormError('Montant trop élevé (max 1 000€)');
            return;
        }

        // Seuls les plats de la carte partent au serveur (il reprend nom et prix de la carte).
        const byId = new Map<string, number>();
        for (const i of items) {
            if (i.menuItemId && i.quantity > 0) byId.set(i.menuItemId, (byId.get(i.menuItemId) ?? 0) + Number(i.quantity));
        }
        const menuItems = Array.from(byId.entries()).map(([menuItemId, quantity]) => ({ menuItemId, quantity }));
        const menuTotal = items
            .filter((i) => i.menuItemId)
            .reduce((acc, i) => acc + Math.round((parseFloat(i.unitPrice) || 0) * 100) * (Number(i.quantity) || 0), 0);
        if (menuTotal > amountCentimes) {
            setFormError('Le montant est inférieur au total des plats de la carte');
            return;
        }

        setCreating(true);
        try {
            const result = await firestoreService.createPendingTransaction({
                restaurantId,
                amount: amountCentimes,
                ...(menuItems.length > 0 ? { items: menuItems } : {}),
                ...(linkedReservationId ? { linkedReservationId } : {}),
            });

            setPendingTxId(result.pendingTransactionId);
            setBillCode(result.code ?? null);
            setCountdown(formatCountdown(result.expiresAt - Date.now()));
            setPosState('waiting');
            startCountdown(result.expiresAt);
            listenTo(result.pendingTransactionId);
        } catch (err: any) {
            setErrorMsg(err?.message ?? 'Erreur lors de la création de la transaction');
            setPosState('error');
        } finally {
            setCreating(false);
        }
    };

    // « Annuler » annule VRAIMENT l'addition côté serveur (plus reliable par personne).
    const handleCancel = async () => {
        if (!pendingTxId) { reset(); return; }
        setCancelling(true);
        try {
            const res = await firestoreService.cancelPendingTransaction(pendingTxId);
            if (!res.cancelled && res.status === 'claimed') {
                // Le client l'a reliée juste avant : l'écouteur affiche la validation.
                return;
            }
            cleanup();
            reset();
        } catch (err: any) {
            setErrorMsg(err?.message ?? 'Annulation impossible. Réessayez.');
        } finally {
            setCancelling(false);
        }
    };

    const reset = () => {
        cleanup();
        setShowQrScanner(false);
        setPosState('input');
        setAmountEuros('');
        setAmountTouched(false);
        setItems([]);
        setFormError('');
        setPendingTxId(null);
        setBillCode(null);
        setSuccessData(null);
        setErrorMsg('');
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="max-w-md mx-auto">
            <h1 className="page-title mb-1">Encaissement fidélité</h1>
            <p className="page-subtitle mb-6">Préparez l'addition, le client valide d'un tap sur la puce.</p>

            {/* ── État 1 : Saisie rapide ── */}
            {posState === 'input' && (
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Montant */}
                        <div>
                            <label htmlFor="pos-amount" className="block text-sm font-bold text-gray-700 mb-2">
                                Montant de l'addition (€)
                            </label>
                            <div className="relative">
                                <input
                                    id="pos-amount"
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    max="999.99"
                                    value={amountEuros}
                                    onChange={(e) => { setAmountTouched(true); setAmountEuros(e.target.value); }}
                                    placeholder="0.00"
                                    required
                                    autoFocus
                                    className="w-full border-2 border-gray-400 rounded-2xl px-5 py-4 text-3xl font-bold text-center focus:border-indigo-500 outline-none focus:ring-1 focus:ring-brand-500"
                                />
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-400">€</span>
                            </div>
                            {amountTouched && items.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setAmountTouched(false)}
                                    className="mt-2 text-xs text-indigo-600 font-bold hover:text-indigo-800"
                                >
                                    Reprendre le total des plats
                                </button>
                            )}
                        </div>

                        {/* Plats depuis la carte (le client les reliera à son compte) */}
                        {menu.length > 0 && (
                            <div>
                                <span className="text-sm font-medium text-gray-500 block mb-2">Plats depuis la carte</span>
                                <DishPicker
                                    menu={menu}
                                    currentItems={catalogItems}
                                    onAdd={addCatalogItem}
                                    onRemove={removeCatalogItem}
                                />
                            </div>
                        )}

                        {/* Extras hors carte : comptés dans le montant seulement */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-500">Extra hors carte (compté dans le montant)</span>
                                <button
                                    type="button"
                                    onClick={addItem}
                                    className="text-xs text-indigo-600 font-bold hover:text-indigo-800"
                                >
                                    + Ajouter
                                </button>
                            </div>
                            {freeItems.length > 0 && (
                                <div className="space-y-2">
                                    {freeItems.map(({ item, idx }) => (
                                        <div key={idx} className="flex gap-2 items-center">
                                            <input
                                                type="text"
                                                value={item.name}
                                                onChange={(e) => updateItem(idx, 'name', e.target.value)}
                                                placeholder="Libellé"
                                                aria-label="Libellé de l'extra"
                                                className="flex-1 border border-gray-400 rounded-lg px-3 py-2 text-sm focus:border-indigo-400 outline-none focus:ring-1 focus:ring-brand-500"
                                            />
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                                                aria-label="Quantité"
                                                className="w-12 border border-gray-400 rounded-lg px-2 py-2 text-sm text-center focus:border-indigo-400 outline-none focus:ring-1 focus:ring-brand-500"
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={item.unitPrice}
                                                onChange={(e) => updateItem(idx, 'unitPrice', e.target.value)}
                                                placeholder="€"
                                                aria-label="Prix unitaire en euros"
                                                className="w-16 border border-gray-400 rounded-lg px-2 py-2 text-sm focus:border-indigo-400 outline-none focus:ring-1 focus:ring-brand-500"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeItem(idx)}
                                                aria-label="Retirer l'extra"
                                                className="text-gray-300 hover:text-red-400 text-lg leading-none"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {formError && (
                            <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{formError}</p>
                        )}

                        <button
                            type="submit"
                            disabled={creating || !amountEuros}
                            className="w-full py-4 bg-brand-500 text-cream font-black text-lg rounded-lg shadow-md hover:bg-brand-600 active:scale-95 transition-transform disabled:opacity-50"
                        >
                            {creating ? 'Création...' : 'Prêt — En attente client'}
                        </button>
                    </form>
                </div>
            )}

            {/* ── État 2 : En attente du client ── */}
            {posState === 'waiting' && pendingTxId && (
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center space-y-6">
                    <div>
                        <p className="text-lg font-bold text-gray-900">En attente du client…</p>
                        <p className="text-3xl text-brand-500 mt-1 font-stat">
                            {(parseFloat(amountEuros) || 0).toFixed(2)} €
                        </p>
                    </div>

                    {billCode && codeSuspended && (
                        <div role="alert" className="bg-amber-50 border border-amber-300 text-amber-900 rounded-2xl px-4 py-3 text-sm font-semibold inline-flex items-start gap-2 text-left">
                            <WarningIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <span>
                                Saisie du code suspendue 15 min suite à trop d'erreurs — utilisez le QR
                                {' '}(jusqu'à {new Date(codeSuspendedUntil).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}).
                            </span>
                        </div>
                    )}

                    {billCode && guestProtected && (
                        <div role="status" className="bg-sky-50 border border-sky-200 text-sky-900 rounded-2xl px-4 py-3 text-sm inline-flex items-start gap-2 text-left">
                            <WarningIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <span>
                                Page web de la puce sous protection (codes erronés répétés) : un client sans l'app
                                {' '}patiente quelques secondes avant que son code soit accepté
                                {' '}(jusqu'à {new Date(guestProtectedUntil).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}).
                            </span>
                        </div>
                    )}

                    {billCode ? (
                        <div className={`bg-indigo-50 rounded-2xl px-6 py-5 ${codeSuspended ? 'opacity-50' : ''}`} aria-live="polite">
                            <p className="text-sm text-gray-600 mb-2">
                                Le client tape la puce NFC, puis saisit ce code sur son téléphone
                                {' '}(avec ou sans l'app) :
                            </p>
                            <p
                                className="text-5xl font-black tracking-[0.15em] text-indigo-700 font-mono whitespace-nowrap"
                                aria-label={`Code de l'addition : ${billCode.split('').join(' ')}`}
                            >
                                {formatBillCode(billCode)}
                            </p>
                        </div>
                    ) : (
                        <div className="bg-gray-50 rounded-2xl px-6 py-4">
                            <p className="text-sm text-gray-600">
                                Le client de la réservation tape la puce NFC : l'addition est reliée à son compte.
                            </p>
                        </div>
                    )}

                    <div className="flex items-center justify-center gap-2" role="timer" aria-live="off">
                        <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" aria-hidden="true" />
                        <p className="text-sm font-bold text-amber-600">Expire dans {countdown}</p>
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowQrScanner(true)}
                        className="w-full py-3 bg-brand-primary text-white font-bold rounded-lg hover:opacity-90"
                    >
                        Pas de NFC ? Scanner le QR du client
                    </button>

                    {errorMsg && <p role="alert" className="text-sm text-red-600">{errorMsg}</p>}

                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="w-full py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        {cancelling ? 'Annulation…' : 'Annuler l\'addition'}
                    </button>
                </div>
            )}

            {showQrScanner && pendingTxId && posState === 'waiting' && (
                <ClientQrScanner
                    pendingTransactionId={pendingTxId}
                    onClose={() => setShowQrScanner(false)}
                />
            )}

            {/* ── État 3 bis : gardée par un client sans l'app (temps réel) ── */}
            {posState === 'success' && successData?.guestUntil && (
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center space-y-6" role="status">
                    <div className="w-20 h-20 mx-auto bg-indigo-50 text-brand-500 rounded-full flex items-center justify-center">
                        <ClockIcon className="w-10 h-10" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-extrabold text-gray-900">Client sans l'app — visite gardée 72 h</h2>
                        <p className="text-sm text-gray-600 mt-2">
                            Il a saisi le code sur la page de la puce. L'addition de
                            {' '}<strong>{(parseFloat(amountEuros) || 0).toFixed(2)} €</strong> lui sera créditée
                            quand il créera son compte dans l'app, jusqu'au {formatUntil(successData.guestUntil)}.
                        </p>
                    </div>
                    <button
                        onClick={reset}
                        className="w-full py-4 bg-brand-500 text-cream font-bold rounded-lg shadow-md hover:bg-brand-600 active:scale-95 transition-transform"
                    >
                        Nouvelle transaction
                    </button>
                </div>
            )}

            {/* ── État 3 : Succès (temps réel) ── */}
            {posState === 'success' && successData && !successData.guestUntil && (
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center space-y-6" role="status">
                    <div className="w-20 h-20 mx-auto bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-10 h-10" />
                    </div>

                    <div>
                        <h2 className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400">Visite validée !</h2>
                        {successData.clientName && (
                            <p className="text-gray-700 dark:text-gray-200 font-semibold mt-1">{successData.clientName}</p>
                        )}
                        {successData.membershipStatus === 'new' && (
                            <p className="text-xs text-brand-primary font-bold mt-1">Nouveau membre</p>
                        )}
                        {successData.membershipStatus === 'reward_unlocked' && (
                            <p className="text-sm text-amber-600 dark:text-amber-400 font-bold mt-1 inline-flex items-center gap-2">
                                <GiftIcon className="w-4 h-4" /> Récompense débloquée !
                            </p>
                        )}
                    </div>

                    <div className="bg-indigo-50 rounded-2xl px-6 py-5">
                        <p className="text-4xl text-brand-500 font-stat">+{successData.pointsAwarded}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            {gainLabel(successData.programType, successData.pointsAwarded)}
                            {successData.newPointsTotal != null && (
                                <> · Total <strong>{successData.newPointsTotal}</strong></>
                            )}
                        </p>
                    </div>

                    <button
                        onClick={reset}
                        className="w-full py-4 bg-brand-500 text-cream font-bold rounded-lg shadow-md hover:bg-brand-600 active:scale-95 transition-transform"
                    >
                        Nouvelle transaction
                    </button>
                </div>
            )}

            {/* ── État 4 : Expiré ── */}
            {posState === 'expired' && (
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center space-y-6" role="status">
                    <div className="w-20 h-20 mx-auto bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center">
                        <ClockIcon className="w-10 h-10" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-amber-600">Addition expirée</h2>
                        <p className="text-sm text-gray-500 mt-2">
                            Le client ne l'a pas reliée dans les 5 minutes.
                        </p>
                    </div>
                    <button
                        onClick={reset}
                        className="w-full py-4 bg-brand-500 text-cream font-bold rounded-lg shadow-md hover:bg-brand-600 active:scale-95 transition-transform"
                    >
                        Réessayer
                    </button>
                </div>
            )}

            {/* ── État 5 : Erreur ── */}
            {posState === 'error' && (
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center space-y-6" role="alert">
                    <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center">
                        <XIcon className="w-10 h-10" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-red-600">Erreur</h2>
                        <p className="text-sm text-gray-500 mt-2">{errorMsg}</p>
                    </div>
                    <button
                        onClick={reset}
                        className="w-full py-3 border-2 border-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-50"
                    >
                        Réessayer
                    </button>
                </div>
            )}
        </div>
    );
};

export default RestaurateurPendingPOS;
